import React, { useEffect, useState } from "react";
import { Card, CircularProgress, Icon } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { get, post } from "utils/WebService";
import { useMaterialUIController } from "context";

import { useUser } from "context/UserContext";

export default function HeaderDevedor({ recordId, onNavigateToDevedor }) {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const { userPerfils } = useUser();

    const isGestorAdminOuCoord = userPerfils && userPerfils.some(p => {
        const name = (typeof p === 'string' ? p : (p?.nome || '')).toLowerCase();
        const tipo = typeof p === 'object' ? p?.tipoPerfil : null;
        return tipo === 1 || tipo === 3 || tipo === 4 || tipo === 6 || 
               name.includes("admin") || name.includes("gestor") || name.includes("coord") || name.includes("suporte");
    });
    const showTimer = !isGestorAdminOuCoord;

    const [devedor, setDevedor] = useState(null);
    const [loading, setLoading] = useState(true);
    const [dataAberturaFicha, setDataAberturaFicha] = useState(null);
    const [currentTime, setCurrentTime] = useState(new Date());
    const [pausaState, setPausaState] = useState({ emPausa: false });

    const fetchPausaState = () => {
        get(`/users/pausa?t=${new Date().getTime()}`).then(res => {
            setPausaState(res);
        }).catch(err => console.error("Erro ao buscar estado de pausa", err));
    };

    const handleTogglePausa = () => {
        post("/users/pausa", {})
            .then(() => {
                fetchPausaState();
                window.dispatchEvent(new Event("pausaToggled"));
            })
            .catch(err => console.error("Erro ao pausar", err));
    };

    useEffect(() => {
        if (!recordId) return;

        const reloadDevedor = () => {
            get(`/devedor/by-id/${recordId}`).then((dataDevedor) => {
                setDevedor(dataDevedor);
            }).catch(err => {
                console.error("Erro ao recarregar HeaderDevedor", err);
            });
        };

        setLoading(true);
        get(`/devedor/by-id/${recordId}`).then((dataDevedor) => {
            setDevedor(dataDevedor);
            setLoading(false);
        }).catch(err => {
            console.error("Erro ao carregar HeaderDevedor", err);
            setLoading(false);
        });

        // Fetch synchronized start time from backend
        get(`/users/devedor/${recordId}/atividade`).then((res) => {
            if (res) {
                setDataAberturaFicha(new Date(res));
            } else {
                setDataAberturaFicha(new Date());
            }
        }).catch(() => {
            setDataAberturaFicha(new Date());
        });

        const handlePausaToggled = (e) => {
            if (e.detail && typeof e.detail.emPausa !== 'undefined') {
                setPausaState(e.detail);
            } else {
                fetchPausaState();
            }
        };
        window.addEventListener("devedorStatusUpdated", reloadDevedor);
        window.addEventListener("pausaToggled", handlePausaToggled);
        
        fetchPausaState();

        return () => {
            window.removeEventListener("devedorStatusUpdated", reloadDevedor);
            window.removeEventListener("pausaToggled", handlePausaToggled);
        };
    }, [recordId]);

    // Timer loop updates currentTime every second
    useEffect(() => {
        const timerId = setInterval(() => {
            if (!pausaState.emPausa) {
                setCurrentTime(new Date());
            }
        }, 1000);

        return () => clearInterval(timerId);
    }, [pausaState.emPausa]);

    const formatTimer = () => {
        if (!dataAberturaFicha) return "00:00";
        const diffMs = currentTime.getTime() - dataAberturaFicha.getTime();
        const totalSeconds = Math.max(0, Math.floor(diffMs / 1000));
        
        const hrs = Math.floor(totalSeconds / 3600);
        const mins = Math.floor((totalSeconds % 3600) / 60);
        const secs = totalSeconds % 60;
        const pad = (val) => String(val).padStart(2, "0");
        if (hrs > 0) {
            return `${pad(hrs)}:${pad(mins)}:${pad(secs)}`;
        }
        return `${pad(mins)}:${pad(secs)}`;
    };

    const renderTermometro = () => {
        if (!devedor) return null;
        
        let forca = devedor.statusFichaForca || 0;
        let color = "#9e9e9e"; // cinza (neutro)
        let fillY = 20;

        if (forca === 1) {
            color = "#03a9f4"; // azul (frio)
            fillY = 16;
        } else if (forca === 2) {
            color = "#ff9800"; // laranja (morno)
            fillY = 11;
        } else if (forca === 3) {
            color = "#f44336"; // vermelho (quente)
            fillY = 6;
        }

        const outlineColor = darkMode ? "#dddddd" : "#212121";

        const svgIcon = (
            <svg width="24" height="32" viewBox="0 0 24 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                {/* Fundo vazio da coluna */}
                <path d="M6.5 21.5 V 6 A 1.5 1.5 0 0 1 9.5 6 V 21.5 Z" fill={darkMode ? "#444" : "#e0e0e0"} />
                
                {/* Preenchimento colorido (Mercúrio) */}
                <rect x="6.5" y={fillY} width="3" height={21.5 - fillY} fill={color} />
                
                {/* Bulbo colorido */}
                <circle cx="8" cy="25" r="3.25" fill={color} />
                
                {/* Reflexo / Brilho no bulbo para dar volume */}
                <path d="M9.5 23.5 A 1.5 1.5 0 0 1 8.5 26.5" stroke="white" strokeWidth="1" strokeLinecap="round" opacity="0.6"/>

                {/* Contorno externo */}
                <path d="M6 21.5 A 4 4 0 1 0 10 21.5 V 6 A 2 2 0 0 0 6 6 Z" stroke={outlineColor} strokeWidth="1.5" fill="none" />
                
                {/* Tracinhos (Escala) */}
                <path d="M13 6h5 M13 9h3 M13 12h5 M13 15h3 M13 18h5 M13 21h3" stroke={outlineColor} strokeWidth="1.5" strokeLinecap="round" />
            </svg>
        );

        return (
            <MDBox display="flex" alignItems="center" ml={2} sx={{ cursor: "help" }} title={`Força: ${forca === 3 ? 'Forte' : forca === 2 ? 'Médio' : forca === 1 ? 'Fraco' : 'Neutro'} | Status atual: ${devedor.statusFichaNome || 'Desconhecido'}`}>
                {svgIcon}
            </MDBox>
        );
    };

    if (loading || !devedor) return <MDBox p={1} display="flex" justifyContent="center"><CircularProgress size={20} /></MDBox>;

    return (
        <Card sx={{ 
            p: 1, 
            px: 2, 
            backgroundColor: darkMode ? "#4B4F5C" : "#f8f9fa", 
            border: `1px solid ${darkMode ? "#333" : "#e0e0e0"}`, 
            boxShadow: "none" 
        }}>
            <MDBox display="flex" flexDirection="column" width="100%">
                <MDBox display="flex" alignItems="center" justifyContent="space-between" width="100%">
                    <MDBox display="flex" alignItems="center" flexWrap="wrap" gap={1}>
                        <Icon sx={{ mr: 0.5, color: "#f4c22b", fontSize: "24px !important" }}>attachment</Icon>
                        <MDTypography variant="body2" fontWeight="medium" color="text">
                            {devedor.unencryptedId ? `${devedor.unencryptedId} - ` : ""}{devedor.nome || "Não informado"}
                        </MDTypography>
                        {devedor.idDevedorPai && (
                            <MDBox
                                component="span"
                                sx={{
                                    px: 1,
                                    py: 0.2,
                                    borderRadius: "4px",
                                    bgcolor: "#ff9800",
                                    color: "#fff",
                                    fontSize: "10px",
                                    fontWeight: "bold",
                                    letterSpacing: "0.5px"
                                }}
                            >
                                EMBARGOS DE TERCEIRO (HONORÁRIO)
                            </MDBox>
                        )}
                        {devedor.devedoresFilhos && devedor.devedoresFilhos.length > 0 && (
                            <MDBox
                                component="span"
                                sx={{
                                    px: 1,
                                    py: 0.2,
                                    borderRadius: "4px",
                                    bgcolor: "#0288d1",
                                    color: "#fff",
                                    fontSize: "10px",
                                    fontWeight: "bold",
                                    letterSpacing: "0.5px"
                                }}
                            >
                                {devedor.devedoresFilhos.length} TERCEIRO(S) VINCULADO(S)
                            </MDBox>
                        )}
                        {renderTermometro()}
                    </MDBox>
                    {showTimer && (
                        <MDBox display="flex" alignItems="center" gap={2}>
                            <MDBox display="flex" alignItems="center" sx={{ bgcolor: darkMode ? "#1a2a3a" : "#e3f2fd", px: 1.5, py: 0.5, borderRadius: "8px" }}>
                                <Icon sx={{ color: pausaState.emPausa ? "#ff9800" : "#1976d2", fontSize: "16px !important", mr: 0.5 }}>
                                    {pausaState.emPausa ? "pause_circle" : "timer"}
                                </Icon>
                                <MDTypography variant="caption" fontWeight="bold" color={pausaState.emPausa ? "warning" : "info"} sx={{ fontFamily: "monospace", fontSize: "12px" }}>
                                    Tempo em atendimento: {formatTimer()} {pausaState.emPausa ? "(EM PAUSA)" : ""}
                                </MDTypography>
                            </MDBox>
                        </MDBox>
                    )}
                </MDBox>
                {devedor.fichas && devedor.fichas.length > 0 && (
                    <MDBox display="flex" flexWrap="wrap" gap={1} mt={1}>
                        {devedor.fichas.map((f, idx) => {
                            const carteiraStr = f.nomeCarteira || f.carteiraNome || (f.isHonorario ? "Honorários" : "Carteira");
                            const statusStr = f.statusFichaNome || f.nomeStatusFicha || "Sem Status";
                            const respStr = f.nomeUsuarioDono || f.nomeUsuario || "Sem Responsável";
                            const isHon = f.isHonorario === true;
                            return (
                                <MDBox 
                                    key={f.rawId || f.id || idx}
                                    sx={{
                                        px: 1.2,
                                        py: 0.4,
                                        borderRadius: "6px",
                                        bgcolor: isHon ? (darkMode ? "#4a3b1a" : "#fff8e1") : (darkMode ? "#333946" : "#eef2f6"),
                                        border: `1px solid ${isHon ? "#ffa000" : (darkMode ? "#555c6d" : "#cfd8dc")}`,
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.8
                                    }}
                                >
                                    <MDTypography variant="caption" fontWeight="bold" sx={{ color: isHon ? "#ff8f00" : (darkMode ? "#90caf9" : "#1976d2"), fontSize: "11px" }}>
                                        [{carteiraStr}]:
                                    </MDTypography>
                                    <MDTypography variant="caption" fontWeight="medium" sx={{ fontSize: "11px", color: darkMode ? "#fff" : "#333" }}>
                                        Status: <strong>{statusStr}</strong> | Resp: <strong>{respStr}</strong>
                                    </MDTypography>
                                </MDBox>
                            );
                        })}
                    </MDBox>
                )}
            </MDBox>
        </Card>
    );
}
